import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronRight, Info, Loader2, Paperclip, Pause, Phone, Play, Voicemail } from 'lucide-react';
import { commsApi } from './phase567Api';
import { OwnerAvatar } from './kit/OwnerChip';
import EmailHtmlFrame from './EmailHtmlFrame';
import { channelIcon, channelLabel, formatBubbleTime, groupByDay } from './inbox/inboxFormat';

// Ticket 152: a GHL send GHL never confirmed. Shown on the bubble and in the
// composer's toast so nobody resends a message that may already be delivered.
export const UNCONFIRMED_HINT = "GoHighLevel did not confirm this send. Don't resend yet — we'll mark it sent or failed once the next sync checks.";

// Ticket 135 S1: "2m 13s" / "45s" — never bare seconds over a minute.
export function formatDuration(durationS) {
  const s = Number(durationS) || 0;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return m > 0 ? `${m}m ${rest}s` : `${rest}s`;
}

// GHL call/voicemail recording is fetched lazily (only on play click) and
// object-URL'd, since <audio src> can't carry the bearer header the rest of
// the app authenticates with.
function RecordingPlayer({ messageId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const fetchAndPlay = async () => {
    setLoading(true);
    setError(false);
    try {
      const blob = await commsApi.getRecording(messageId);
      setUrl(URL.createObjectURL(blob));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    return <audio controls autoPlay src={url} className="w-full max-w-xs mt-2" data-testid={`recording-player-${messageId}`} />;
  }
  return (
    <button
      type="button"
      onClick={fetchAndPlay}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs mt-2 px-2 py-1 rounded-full border border-current/20 hover:bg-black/5 disabled:opacity-60"
      data-testid={`recording-play-btn-${messageId}`}
    >
      {loading ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
      {error ? 'Retry recording' : 'Play recording'}
    </button>
  );
}

// Ticket 106: "1:05" from seconds into the call; '' when GHL gave no time.
export function formatOffset(seconds) {
  if (seconds === null || seconds === undefined || seconds === '') return '';
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '';
  const whole = Math.floor(s);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

// Ticket 106: GoHighLevel's own transcript of the recording, redacted when
// BFY stored it. Collapsed by default. GHL's two audio channels don't map to
// a fixed caller, so lines carry a time, not a speaker name.
function CallTranscript({ m }) {
  const [open, setOpen] = useState(false);
  const t = m.transcript;
  if (!t || t.status !== 'ready') return null;
  const segments = Array.isArray(t.segments) && t.segments.length
    ? t.segments
    : [{ start: null, text: t.text || '' }];
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
        data-testid={`transcript-toggle-${m.message_id}`}
      >
        <Chevron size={12} />
        {open ? 'Hide transcript' : 'Show transcript'}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1 text-xs leading-snug max-h-64 overflow-y-auto pr-1" data-testid={`transcript-${m.message_id}`}>
          {segments.map((seg, i) => (
            <li key={i} className="flex gap-2">
              {formatOffset(seg.start) && <span className="opacity-60 tabular-nums shrink-0">{formatOffset(seg.start)}</span>}
              <span className="whitespace-pre-wrap break-words">{seg.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CallCard({ m }) {
  const call = m.ghl?.call || {};
  const isVoicemail = m.channel === 'voicemail';
  const Icon = isVoicemail ? Voicemail : Phone;
  const DirIcon = m.direction === 'outbound' ? ArrowUpRight : ArrowDownLeft;
  return (
    <div className="min-w-[180px]">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon size={14} />
        <DirIcon size={12} className="opacity-70" />
        <span>{isVoicemail ? 'Voicemail' : 'Call'}</span>
      </div>
      <div className="text-xs opacity-80 mt-1 flex flex-wrap items-center gap-x-2">
        {call.call_status && <span className="capitalize">{call.call_status.replace(/_/g, ' ')}</span>}
        {typeof call.duration_s === 'number' && <span>{formatDuration(call.duration_s)}</span>}
      </div>
      {call.has_recording && <RecordingPlayer messageId={m.message_id} />}
      <CallTranscript m={m} />
    </div>
  );
}

function EmailBody({ m }) {
  const email = m.ghl?.email || {};
  // A row BFY sent carries its own (server-sanitised) html until GHL's copy syncs in.
  const raw = email.html || m.html;
  const html = typeof raw === 'string' && raw.trim() ? raw : null;
  return (
    <div>
      {email.subject && <div className="text-sm font-semibold mb-1">{email.subject}</div>}
      {/* No sanitiser is wired into this app: HTML only ever goes through the
          sandboxed frame, never dangerouslySetInnerHTML. Plain-text emails
          (no html) render as text. */}
      {html ? (
        <EmailHtmlFrame html={html} messageId={m.message_id} />
      ) : (
        <div className="text-sm whitespace-pre-wrap break-words">{m.body || ''}</div>
      )}
    </div>
  );
}

// Ticket 195: inbound photos and files stay in GoHighLevel; BFY shows only
// a link to GHL's copy. https only (the sync drops anything else too).
export function attachmentName(url, i) {
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop();
    if (last) return decodeURIComponent(last);
  } catch { /* fall through */ }
  return `Attachment ${i + 1}`;
}

function Attachments({ m }) {
  const urls = (m.ghl?.attachments || []).filter((u) => typeof u === 'string' && /^https:\/\//i.test(u));
  if (!urls.length) return null;
  return (
    <ul className="mt-1.5 space-y-1" data-testid={`msg-attachments-${m.message_id}`}>
      {urls.map((url, i) => (
        <li key={url}>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs underline underline-offset-2 break-all"
            title="Opens GoHighLevel's copy in a new tab">
            <Paperclip size={12} className="shrink-0" />
            {attachmentName(url, i)}
          </a>
        </li>
      ))}
    </ul>
  );
}

// Ticket 162: meta line reads "SMS · Inbound · 9:05 AM", never raw keys.
export function directionLabel(direction) {
  const d = String(direction || '');
  return d ? d.charAt(0).toUpperCase() + d.slice(1) : '';
}

// Ticket 164: who sent it, GHL-style — the contact's initials circle (inbound)
// or a brand circle (outbound), each with a small channel icon badge. This is
// where the channel shows; the bubble itself carries only the message.
function BubbleAvatar({ m, contactId, contactName }) {
  const Icon = channelIcon(m.channel);
  const outbound = m.direction === 'outbound';
  return (
    <span className="relative flex-shrink-0 mb-4" data-testid={`msg-avatar-${m.message_id}`}>
      {outbound ? (
        <span className="inline-flex w-6 h-6 rounded-full items-center justify-center" style={{ background: 'rgba(7, 11, 95, 0.1)', color: '#070B5F' }} aria-hidden="true">
          <Icon size={12} />
        </span>
      ) : (
        <OwnerAvatar userId={contactId || m.contact_id || 'contact'} name={contactName} size="sm" />
      )}
      <span
        className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500"
        title={channelLabel(m.channel)}
        aria-label={channelLabel(m.channel)}
        data-testid={`msg-channel-${m.message_id}`}
      >
        <Icon size={8} />
      </span>
    </span>
  );
}

// Ticket 164: centered "Today" / "Yesterday" / "Sep 28" pill between days.
export function DayDivider({ label }) {
  if (!label) return null;
  return (
    <div className="flex justify-center py-1" data-testid="day-divider">
      <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-3 py-1">{label}</span>
    </div>
  );
}

// Ordered messages → bubbles with a day pill wherever the calendar day changes.
export function MessageList({ messages, contactId, contactName, align = true, onDetails }) {
  return groupByDay(messages).map((g, i) => (
    <React.Fragment key={g.key || `nodate-${i}`}>
      <DayDivider label={g.label} />
      {g.items.map((m) => (
        <MessageItem key={m.message_id} m={m} align={align} contactId={contactId} contactName={contactName} onDetails={onDetails} />
      ))}
    </React.Fragment>
  ));
}

// Shared message bubble used by both the Inbox thread view and
// ContactCommunications. `align` controls left/right placement. The meta
// line (time, status, GHL) sits under the bubble, outside it.
export default function MessageItem({ m, align = true, contactId, contactName, onDetails }) {
  const outbound = m.direction === 'outbound';
  const right = align && outbound;
  const isCallLike = m.channel === 'call' || m.channel === 'voicemail';
  const isEmail = m.channel === 'email';

  const noteAlign = right ? 'items-end text-right' : 'items-start text-left';
  const showNote = m.status === 'unconfirmed' || m.status === 'failed';
  const failedText = m.error ? `Not sent \u2014 ${m.error}` : 'Not sent';
  const avatar = <BubbleAvatar m={m} contactId={contactId} contactName={contactName} />;

  return (
    <div className={`flex items-end gap-2 ${right ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.message_id}`}>
      {!right && avatar}
      {/* 70% resolves against this full-width row, never a shrink-wrapped parent. */}
      <div className={`flex flex-col ${noteAlign} min-w-0 max-w-full sm:max-w-[70%]`}>
        <div
          className={`max-w-full rounded-2xl px-4 py-2.5 ${outbound ? 'text-white' : 'bg-slate-100 text-slate-700'}`}
          style={outbound ? { background: '#070B5F' } : undefined}
        >
          {isCallLike ? (
            <CallCard m={m} />
          ) : isEmail ? (
            <EmailBody m={m} />
          ) : (
            <div className="text-sm break-words">{m.body}</div>
          )}
          {!isCallLike && <Attachments m={m} />}
          {m.note && <div className="text-[10px] opacity-70 mt-1 italic">{m.note}</div>}
          {m.keyword_action && <div className="text-[10px] opacity-70 mt-1 italic">consent action: {m.keyword_action}</div>}
        </div>
        <div className="mt-0.5 px-1 text-[10px] text-slate-400 flex items-center gap-1.5 whitespace-nowrap" data-testid={`msg-meta-${m.message_id}`}>
          <span>{formatBubbleTime(m.created_at)}</span>
          {m.status === 'unconfirmed' && (
            <span className="text-[10px] leading-tight px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-100 text-amber-900 font-medium"
              title={UNCONFIRMED_HINT} data-testid={`msg-status-${m.message_id}`}>
              Unconfirmed
            </span>
          )}
          {m.status === 'failed' && (
            <span className="text-[10px] leading-tight px-1.5 py-0.5 rounded-full border border-red-300 bg-red-100 text-red-800 font-medium"
              title={m.error || undefined} data-testid={`msg-status-${m.message_id}`}>
              Failed
            </span>
          )}
          {m.source === 'ghl' && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-slate-300 text-slate-500" data-testid={`ghl-badge-${m.message_id}`}>
              GHL
            </span>
          )}
          {onDetails && (
            <button type="button" onClick={() => onDetails(m)} aria-label="Message details" title="Message details"
              className="text-slate-400 hover:text-slate-600" data-testid={`msg-details-btn-${m.message_id}`}>
              <Info size={11} />
            </button>
          )}
        </div>
        {showNote && (
          m.status === 'unconfirmed' ? (
            <div className="px-1 text-xs leading-snug text-amber-700" data-testid={`msg-status-note-${m.message_id}`}>
              {UNCONFIRMED_HINT}
            </div>
          ) : (
            <div className="px-1 text-xs leading-snug text-red-700 max-w-full truncate"
              title={failedText} data-testid={`msg-status-note-${m.message_id}`}>
              {failedText}
            </div>
          )
        )}
      </div>
      {right && avatar}
    </div>
  );
}
